/**
 * EnrolLotModal — enrol a new QC lot (POST /qc/lots, cap qc.lot.manage). The lot lands as `brouillon`
 * (editable); cible/ecart_type are optional here and FROZEN later via the valider step. Keyboard +
 * AT friendly (native date input, FormField wiring); gated by useCan.
 */
import { useEffect, useState, type FormEvent } from "react";
import { Button, FormField, Input, Modal, Select, useToast } from "@/ui";
import { useCan } from "@/auth";
import { useT } from "@/app";
import { ApiError } from "@/api";
import { QC_CAPS } from "../caps";
import { useEnrolLot, useLabId } from "../api";
import { NIVEAUX, type Niveau } from "../types";
import { parseNum } from "../lib";

export interface EnrolLotModalProps {
  open: boolean;
  onClose: () => void;
  onEnrolled: () => void;
}

export function EnrolLotModal({ open, onClose, onEnrolled }: EnrolLotModalProps) {
  const { t } = useT();
  const toast = useToast();
  const labId = useLabId();
  const canManage = useCan(QC_CAPS.lotManage);
  const mutation = useEnrolLot(labId);

  const [code, setCode] = useState("");
  const [parametre, setParametre] = useState("");
  const [niveau, setNiveau] = useState<Niveau>("niveau_1");
  const [fabricant, setFabricant] = useState("");
  const [automate, setAutomate] = useState("");
  const [unite, setUnite] = useState("");
  const [peremption, setPeremption] = useState("");
  const [cible, setCible] = useState("");
  const [sd, setSd] = useState("");
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCode("");
    setParametre("");
    setNiveau("niveau_1");
    setFabricant("");
    setAutomate("");
    setUnite("");
    setPeremption("");
    setCible("");
    setSd("");
    setTouched(false);
  }, [open]);

  const codeError = touched && code.trim() === "" ? t("qc.field.required") : null;
  const parametreError = touched && parametre.trim() === "" ? t("qc.field.required") : null;
  const peremptionError = touched && peremption.trim() === "" ? t("qc.field.dateInvalid") : null;
  const cibleError = touched && cible.trim() !== "" && parseNum(cible) == null ? t("qc.field.numInvalid") : null;
  const sdError = touched && sd.trim() !== "" && parseNum(sd) == null ? t("qc.field.numInvalid") : null;

  const valid =
    code.trim() !== "" &&
    parametre.trim() !== "" &&
    peremption.trim() !== "" &&
    (cible.trim() === "" || parseNum(cible) != null) &&
    (sd.trim() === "" || parseNum(sd) != null);

  const handleClose = () => {
    if (mutation.isPending) return;
    onClose();
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!valid || !canManage) return;
    const cibleNum = parseNum(cible);
    const sdNum = parseNum(sd);
    mutation.mutate(
      {
        code: code.trim(),
        parametre_code: parametre.trim(),
        niveau,
        date_peremption: peremption,
        ...(fabricant.trim() ? { fabricant: fabricant.trim() } : {}),
        ...(automate.trim() ? { automate_id: automate.trim() } : {}),
        ...(unite.trim() ? { unite: unite.trim() } : {}),
        ...(cibleNum != null ? { cible: cibleNum } : {}),
        ...(sdNum != null ? { ecart_type: sdNum } : {}),
      },
      {
        onSuccess: () => {
          toast.success(t("qc.lots.enrol.success"));
          onEnrolled();
        },
        onError: (err) => toast.error(err instanceof ApiError ? err.message : t("qc.field.required")),
      },
    );
  };

  const niveauOptions = NIVEAUX.map((n) => ({ value: n, label: t(`qc.niveau.${n}`) }));

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={t("qc.lots.enrol.title")}
      footer={
        <>
          <Button variant="ghost" onClick={handleClose} disabled={mutation.isPending}>
            {t("qc.action.cancel")}
          </Button>
          <Button
            type="submit"
            form="qc-enrol-lot"
            loading={mutation.isPending}
            disabled={!canManage || !valid}
          >
            {t("qc.lots.enrol.submit")}
          </Button>
        </>
      }
    >
      <form id="qc-enrol-lot" onSubmit={submit} noValidate>
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-4)" }}>
          <FormField
            label={t("qc.lots.field.code")}
            hint={t("qc.lots.field.code.hint")}
            required
            {...(codeError ? { error: codeError } : {})}
          >
            {(p) => (
              <Input
                {...p}
                autoFocus
                mono
                autoComplete="off"
                value={code}
                invalid={!!codeError}
                onChange={(e) => setCode(e.target.value)}
              />
            )}
          </FormField>

          <FormField label={t("qc.parametre")} required {...(parametreError ? { error: parametreError } : {})}>
            {(p) => (
              <Input
                {...p}
                mono
                autoComplete="off"
                placeholder={t("qc.parametre.placeholder")}
                value={parametre}
                invalid={!!parametreError}
                onChange={(e) => setParametre(e.target.value)}
              />
            )}
          </FormField>

          <FormField label={t("qc.niveau")} required>
            {(p) => (
              <Select
                {...p}
                options={niveauOptions}
                value={niveau}
                onChange={(e) => setNiveau(e.target.value as Niveau)}
              />
            )}
          </FormField>

          <FormField label={`${t("qc.lots.field.fabricant")} (${t("qc.field.optional")})`}>
            {(p) => (
              <Input {...p} autoComplete="off" value={fabricant} onChange={(e) => setFabricant(e.target.value)} />
            )}
          </FormField>

          <FormField label={`${t("qc.automate")} (${t("qc.field.optional")})`}>
            {(p) => (
              <Input
                {...p}
                mono
                inputMode="numeric"
                autoComplete="off"
                placeholder={t("qc.automate.placeholder")}
                value={automate}
                onChange={(e) => setAutomate(e.target.value)}
              />
            )}
          </FormField>

          <FormField label={t("qc.lots.field.peremption")} required {...(peremptionError ? { error: peremptionError } : {})}>
            {(p) => (
              <Input
                {...p}
                type="date"
                value={peremption}
                invalid={!!peremptionError}
                onChange={(e) => setPeremption(e.target.value)}
              />
            )}
          </FormField>

          <FormField label={`${t("qc.lots.field.unite")} (${t("qc.field.optional")})`}>
            {(p) => <Input {...p} autoComplete="off" value={unite} onChange={(e) => setUnite(e.target.value)} />}
          </FormField>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--nv-s-3)" }}>
            <FormField
              label={t("qc.lots.field.cible")}
              hint={t("qc.lots.field.cible.hint")}
              {...(cibleError ? { error: cibleError } : {})}
            >
              {(p) => (
                <Input
                  {...p}
                  mono
                  inputMode="decimal"
                  value={cible}
                  invalid={!!cibleError}
                  onChange={(e) => setCible(e.target.value)}
                />
              )}
            </FormField>

            <FormField
              label={t("qc.lots.field.sd")}
              hint={t("qc.lots.field.sd.hint")}
              {...(sdError ? { error: sdError } : {})}
            >
              {(p) => (
                <Input
                  {...p}
                  mono
                  inputMode="decimal"
                  value={sd}
                  invalid={!!sdError}
                  onChange={(e) => setSd(e.target.value)}
                />
              )}
            </FormField>
          </div>
        </div>
      </form>
    </Modal>
  );
}
